import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useController, useFormContext, type Control, type FieldValues } from 'react-hook-form';
import Alert from '@amzn/meridian/alert';
import Button from '@amzn/meridian/button';
import Card from '@amzn/meridian/card';
import Checkbox from '@amzn/meridian/checkbox';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Input from '@amzn/meridian/input';
import Loader from '@amzn/meridian/loader';
import Popover from '@amzn/meridian/popover';
import Row from '@amzn/meridian/row';
import Select, { SelectOption } from '@amzn/meridian/select';
import Text from '@amzn/meridian/text';
import viewTokens from '@amzn/meridian-tokens/base/icon/view';
import searchTokens from '@amzn/meridian-tokens/base/icon/search';
import { useTranslation } from 'src/arb-wrapper';
import { SONATA_PAGE_TYPES, COUNTRY_TO_DOMAIN, DEFAULT_PRIMEVIDEO_DOMAIN, DESTINATION_LINK_TYPES } from 'src/constants';
import { type DetailPageLinkConfig, type HomePageLinkConfig, type LandingPageLinkConfig } from 'src/store/generated/leela-api-generated';
import { FormPageIdSelect } from 'src/components/campaigns/forms/FormPageIdSelect';
import { parsePrimeVideoUrl, type ParsedPageParams } from 'src/utils/parsePrimeVideoUrl';
import { leelaApi } from 'src/store/leela-api';

// Type alias for destination link config union
type DestinationLinkConfig = DetailPageLinkConfig | HomePageLinkConfig | LandingPageLinkConfig;
type DestinationLinkType = typeof DESTINATION_LINK_TYPES[keyof typeof DESTINATION_LINK_TYPES];

export interface DestinationLinkConfigurationProps {
  control: Control<FieldValues> | undefined;
  name: string;
  index: number;
  isFirstMultiTitleTemplate: boolean;
  useForAll: boolean;
  onUseForAllChange: (value: boolean) => void;
}

export const DestinationLinkConfiguration: React.FC<DestinationLinkConfigurationProps> = ({
  control,
  name,
  index,
  isFirstMultiTitleTemplate,
  useForAll,
  onUseForAllChange,
}) => {
  const { t } = useTranslation();
  const { setValue, watch } = useFormContext();

  // --- URL detection: parsed result triggers Sonata validation via RTK Query ---
  const [pendingUrlResult, setPendingUrlResult] = useState<ParsedPageParams | null>(null);
  const { data: validationPageIds, isFetching: isValidating } = leelaApi.useSonataApiControllerSearchPageIdsQuery(
    { pageType: pendingUrlResult?.pageType ?? '' },
    { skip: !pendingUrlResult }
  );

  // Get country from product strategy
  const productStrategyPath = name.split('.').slice(0, 2).join('.');
  const country = watch(`${productStrategyPath}.country`) as string;

  useController({
    control,
    name,
    defaultValue: { type: DESTINATION_LINK_TYPES.HOME },
  });

  const pageTypeFieldName = `${name}.pageType`;
  const { fieldState: pageTypeFieldState } = useController({
    control,
    name: pageTypeFieldName,
    rules: {
      validate: (value) => {
        if (linkConfig?.type === DESTINATION_LINK_TYPES.LANDING && !value) {
          return t('form_validation_required_field');
        }
        return true;
      },
    },
  });

  const pageIdFieldName = `${name}.pageId`;

  const linkConfig = watch(name) as DestinationLinkConfig;
  const pageId = watch(pageIdFieldName) as string;
  const isLanding = linkConfig?.type === DESTINATION_LINK_TYPES.LANDING;
  const selectedPageType = isLanding && 'pageType' in linkConfig ? linkConfig.pageType : '';

  /** Handles switching between HOME, LANDING, and DETAIL link types */
  const handleTypeChange = useCallback((type: DestinationLinkType) => {
    if (type === DESTINATION_LINK_TYPES.HOME) {
      setValue(name, { type: DESTINATION_LINK_TYPES.HOME });
    } else if (type === DESTINATION_LINK_TYPES.DETAIL) {
      setValue(name, { type: DESTINATION_LINK_TYPES.DETAIL });
    } else {
      setValue(name, { type: DESTINATION_LINK_TYPES.LANDING, pageType: '', pageId: '' });
    }
  }, [name, setValue]);

  /** Updates pageType for LANDING link config */
  const handlePageTypeChange = useCallback((pageType: string) => {
    if (linkConfig?.type === DESTINATION_LINK_TYPES.LANDING) {
      setValue(name, { ...linkConfig, pageType });
    }
  }, [name, setValue, linkConfig]);

  // --- URL detect state ---
  const [urlInputValue, setUrlInputValue] = useState('');
  const [urlParseError, setUrlParseError] = useState('');
  const urlValidationProcessedRef = useRef(false);

  /** Phase 1: Parse URL and trigger Sonata validation */
  const handleUrlDetect = useCallback(() => {
    if (!urlInputValue.trim()) {
      setUrlParseError(t('form_url_parse_empty'));
      return;
    }
    const result = parsePrimeVideoUrl(urlInputValue.trim());
    if (!result) {
      setUrlParseError(t('form_url_parse_invalid'));
      return;
    }
    const validPageTypes = SONATA_PAGE_TYPES.map(pt => pt.toLowerCase());
    if (!validPageTypes.includes(result.pageType)) {
      setUrlParseError(t('form_url_parse_unsupported_page_type'));
      return;
    }
    // Trigger Sonata validation by setting pendingUrlResult
    setUrlParseError('');
    urlValidationProcessedRef.current = false;
    setPendingUrlResult(result);
  }, [urlInputValue, t]);

  /** Phase 2: When Sonata data arrives, validate pageId and auto-fill (runs once per detect) */
  React.useEffect(() => {
    if (!pendingUrlResult || isValidating || urlValidationProcessedRef.current) return;
    urlValidationProcessedRef.current = true;

    if (!validationPageIds) {
      setUrlParseError(t('form_url_parse_validation_error'));
      setPendingUrlResult(null);
      return;
    }
    if (!validationPageIds.includes(pendingUrlResult.pageId)) {
      setUrlParseError(t('form_url_parse_page_id_not_found'));
      setPendingUrlResult(null);
      return;
    }
    // Success: auto-fill form fields
    setValue(name, { type: DESTINATION_LINK_TYPES.LANDING, pageType: pendingUrlResult.pageType, pageId: pendingUrlResult.pageId });
    setValue(pageIdFieldName, pendingUrlResult.pageId, { shouldValidate: true });
    setUrlParseError('');
    setPendingUrlResult(null);
  }, [pendingUrlResult, validationPageIds, isValidating, name, pageIdFieldName, setValue, t]);

  /** Generates Prime Video URL based on link config and country */
  const generatePreviewUrl = useCallback(() => {
    const baseDomain = country ? (COUNTRY_TO_DOMAIN[country] || DEFAULT_PRIMEVIDEO_DOMAIN) : DEFAULT_PRIMEVIDEO_DOMAIN;

    if (linkConfig?.type === DESTINATION_LINK_TYPES.HOME) {
      return `${baseDomain}/home?r=web`;
    }

    if (linkConfig?.type === DESTINATION_LINK_TYPES.LANDING) {
      const pageType = 'pageType' in linkConfig ? linkConfig.pageType : '';
      if (pageType && pageId) {
        return `${baseDomain}/landing?r=web&pageId=${pageId}&pageType=${pageType}`;
      }
    }

    return null;
  }, [linkConfig, country, pageId]);

  /** Opens preview URL in new tab */
  const handlePreview = useCallback(() => {
    const url = generatePreviewUrl();
    if (url) {
      window.open(url, '_blank');
    }
  }, [generatePreviewUrl]);

  /** Determines if preview button should be shown */
  const canPreview = useMemo(() => {
    if (linkConfig?.type === DESTINATION_LINK_TYPES.HOME) return true;
    if (linkConfig?.type === DESTINATION_LINK_TYPES.LANDING) {
      const pageType = 'pageType' in linkConfig ? linkConfig.pageType : '';
      return !!(pageType && pageId);
    }
    return false;
  }, [linkConfig, pageId]);

  const [popoverIsVisible, setPopoverIsVisible] = useState(false);
  const popoverButtonRef = useRef<HTMLButtonElement>(null);

  const showPopover = useCallback(() => {
    setPopoverIsVisible(true);
  }, []);

  const hidePopover = useCallback(() => {
    setPopoverIsVisible(false);
  }, []);

  return (
    <Card spacingInset="400">
      <Column spacing="200">
        <Text>{t('form_destination_link_configuration')}</Text>
        <Select
          data-testid={`destination-link-type-${index}`}
          label={t('form_destination_link_type')}
          value={linkConfig?.type || DESTINATION_LINK_TYPES.HOME}
          onChange={handleTypeChange}
        >
          <SelectOption label={t('form_destination_link_home')} value={DESTINATION_LINK_TYPES.HOME} />
          <SelectOption label={t('form_destination_link_landing')} value={DESTINATION_LINK_TYPES.LANDING} />
          <SelectOption label={t('form_destination_link_detail')} value={DESTINATION_LINK_TYPES.DETAIL} />
        </Select>

        {isLanding && (
          <Column spacing="200">
            <Text>{t('form_fill_from_url_header')}</Text>
            <Input
              data-testid={`url-input-${index}`}
              placeholder="https://www.primevideo.com/genre/..."
              suffix={
                <Button
                  data-testid={`url-detect-btn-${index}`}
                  disabled={!!pendingUrlResult && isValidating}
                  type="icon"
                  onClick={handleUrlDetect}
                >
                  {pendingUrlResult && isValidating ? <Loader size="small" /> : <Icon tokens={searchTokens} />}
                </Button>
              }
              value={urlInputValue}
              onChange={setUrlInputValue}
            />
            {urlParseError && (
              <Alert data-testid={`url-parse-error-${index}`} size="small" type="error">{urlParseError}</Alert>
            )}
            <Select
              data-testid={`page-type-${index}`}
              error={!!pageTypeFieldState.error}
              errorMessage={pageTypeFieldState.error?.message}
              label={`${t('form_page_type')}*`}
              placeholder={t('form_page_type_placeholder')}
              value={'pageType' in linkConfig ? linkConfig.pageType || '' : ''}
              onChange={handlePageTypeChange}
            >
              {SONATA_PAGE_TYPES.map(pageType => (
                <SelectOption key={pageType} label={pageType} value={pageType.toLowerCase()} />
              ))}
            </Select>
            <FormPageIdSelect
              control={control}
              index={index}
              name={pageIdFieldName}
              pageType={selectedPageType}
              required
            />
          </Column>
        )}

        <Row alignmentVertical="center" spacing="200" widths={['fill', 'fit']}>
          {isFirstMultiTitleTemplate
            ? (
            <Checkbox
              checked={useForAll}
              data-testid={`use-destination-config-for-all-${index}`}
              onChange={onUseForAllChange}
            >
              {t('form_use_destination_config_for_all_multi_title')}
            </Checkbox>
              )
            : (
            <div />
              )}
          {canPreview && (
            <>
              <Button
                aria-expanded={popoverIsVisible}
                aria-haspopup="dialog"
                aria-label={t('form_preview_url')}
                data-testid={`preview-url-${index}`}
                ref={popoverButtonRef}
                type="icon"
                onClick={handlePreview}
                onMouseEnter={showPopover}
                onMouseLeave={hidePopover}
              >
                <Icon tokens={viewTokens} />
              </Button>
              <Popover
                anchorNode={popoverButtonRef.current ?? undefined}
                open={popoverIsVisible}
                position="bottom"
                onClose={hidePopover}
              >
                <Text type="b200">{generatePreviewUrl()}</Text>
              </Popover>
            </>
          )}
        </Row>
      </Column>
    </Card>
  );
};
